import { datadogLogs } from '@datadog/browser-logs';
import React from 'react';

import environment from '../environment';

type Extras = { extra?: Record<string, unknown>; tags?: Record<string, string> };

const isInitialized = (() => {
  const token = environment.datadogClientToken;
  if (!token) {
    return false;
  }

  datadogLogs.init({
    clientToken: token,
    site: 'datadoghq.eu',
    service: 'design-editor',
    env: environment.env,
    version: process.env.REACT_APP_VERSION,
    forwardErrorsToLogs: false,
    forwardConsoleLogs: [],
    sessionSampleRate: 100,
  });

  return true;
})();

export function captureException(error: unknown, context?: Extras): void {
  if (isInitialized) {
    const message = error instanceof Error ? error.message : String(error);
    datadogLogs.logger.error(message, { error, ...context?.extra, ...context?.tags });
  }
  // eslint-disable-next-line no-console
  console.error('[logger] captureException', error, context);
}

export function captureMessage(message: string, context?: Extras): void {
  if (isInitialized) {
    datadogLogs.logger.warn(message, { ...context?.extra, ...context?.tags });
  }
  // eslint-disable-next-line no-console
  console.warn('[logger] captureMessage', message, context);
}

interface ErrorBoundaryState {
  hasError: boolean;
}

type ErrorBoundaryProps = React.PropsWithChildren<unknown>;

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureException(error, { extra: { componentStack: info.componentStack } });
  }

  override render(): React.ReactNode {
    return this.state.hasError ? null : this.props.children;
  }
}
